import { useState, type JSX } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Typography from '@mui/material/Typography'
import type { ColumnMapping, CreateJobResponse, JobSettings } from '@profiler/shared'
import { validationFromError } from '../api'
import { useApi } from '../auth'
import { qk, useMe } from '../queries'
import { StepMapping } from './newjob/StepMapping'
import { StepReview } from './newjob/StepReview'
import { MAX_NAME_LENGTH, StepSettings } from './newjob/StepSettings'
import { StepUpload } from './newjob/StepUpload'
import { checkMapping, defaultSettings, hasBlockingIssue } from './newjob/state'
import { usePageTitle } from '../usePageTitle'

const STEPS = ['Upload', 'Map columns', 'Settings', 'Review and start'] as const

export function NewJobPage(): JSX.Element {
  usePageTitle('New job')

  const api = useApi()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const me = useMe()

  const [searchParams, setSearchParams] = useSearchParams()
  const [created, setCreated] = useState<CreateJobResponse | null>(null)
  const [mapping, setMapping] = useState<ColumnMapping>({})
  const [settings, setSettings] = useState<JobSettings>(defaultSettings)
  const [name, setName] = useState('')

  /*
   * The step is in the URL so that Back walks the wizard rather than leaving it:
   * the upload has already created a job server-side by then, and a wizard
   * abandoned at step three strands that job as a DRAFT with no route back in.
   *
   * Everything else stays in memory, so a step deep-linked or reloaded with no
   * upload behind it has nothing to show and falls back to the upload.
   */
  const requestedStep = Number.parseInt(searchParams.get('step') ?? '', 10)
  const step =
    created === null || !Number.isFinite(requestedStep)
      ? 0
      : Math.min(Math.max(requestedStep, 0), STEPS.length - 1)

  function goToStep(next: number): void {
    setSearchParams(next === 0 ? {} : { step: String(next) })
  }

  const jobId = created?.jobId ?? ''

  /**
   * The confirm route is what runs the validator, so the review step gets its
   * report by staging the job rather than by asking a separate endpoint. That is
   * safe to repeat: confirming a DRAFT or READY job rewrites the staged company
   * rows and spends nothing. Credits are only committed by Start.
   *
   * The mapping and the settings are both inputs to the report — change
   * people-per-company and the estimate moves — so both belong in the key.
   */
  const staged = useQuery({
    queryKey: ['confirm', jobId, mapping, settings, name] as const,
    queryFn: () =>
      api.confirmJob(jobId, {
        name: name.trim() || created?.name || 'Untitled job',
        columnMapping: mapping,
        settings,
      }),
    enabled: step === 3 && jobId !== '',
    staleTime: 0,
    retry: false,
  })

  const start = useMutation({
    mutationFn: () => api.startJob(jobId),
    onSuccess: async (job) => {
      await queryClient.invalidateQueries({ queryKey: qk.jobs })
      navigate(`/jobs/${job.jobId}`)
    },
  })

  const nextBlocked =
    (step === 0 && created === null) || (step === 1 && hasBlockingIssue(checkMapping(mapping)))

  // A confirm rejected for having no usable row still carries the report that
  // explains which rows were blocked, and that is exactly what the user needs.
  const rejectedReport = validationFromError(staged.error)

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5">New job</Typography>
        <Typography variant="body2" color="text.secondary">
          Nothing is charged until you press Start on the last step.
        </Typography>
      </Box>

      <Stepper activeStep={step} alternativeLabel>
        {STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: 3 }}>
          {step === 0 ? (
            <StepUpload
              created={created}
              onCreated={(response, file) => {
                setCreated(response)
                setMapping(response.suggestedMapping)
                // A long spreadsheet filename would otherwise pre-fill a name the
                // confirm on the last step rejects.
                setName((response.name || file.name).slice(0, MAX_NAME_LENGTH))
                // A step left in the URL by an earlier upload would jump this one
                // straight past the mapping it has just suggested.
                setSearchParams({}, { replace: true })
              }}
            />
          ) : null}

          {step === 1 && created ? (
            <StepMapping created={created} mapping={mapping} onChange={setMapping} />
          ) : null}

          {step === 2 ? (
            <StepSettings name={name} onNameChange={setName} settings={settings} onChange={setSettings} />
          ) : null}

          {step === 3 ? (
            <StepReview
              report={staged.data?.validation ?? rejectedReport ?? undefined}
              isLoading={staged.isPending}
              error={rejectedReport ? null : staged.error}
              onRetry={() => void staged.refetch()}
              creditBalance={me.data?.creditBalance ?? null}
              onStart={() => start.mutate()}
              isStarting={start.isPending}
              startError={start.error}
            />
          ) : null}
        </CardContent>
      </Card>

      <Stack direction="row" spacing={1}>
        <Button
          onClick={() => (step === 0 ? navigate('/jobs') : goToStep(step - 1))}
          disabled={start.isPending}
        >
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {step < STEPS.length - 1 ? (
          <Button variant="contained" disabled={nextBlocked} onClick={() => goToStep(step + 1)}>
            Next
          </Button>
        ) : null}
      </Stack>
    </Stack>
  )
}
